import {
  CREATE_INPUT_DOC_API_URL,
  DELETE_INPUT_DOC_API_URL,
  DELETE_INPUT_STEP_API_URL,
  DELETE_INPUT_TASK_API_URL,
  GET_ACTIVE_INPUTS_BY_ORG_API_URL,
  GET_INPUT_BY_ID_API_URL,
  UPDATE_INPUT_DOC_API_URL,
  UPSERT_ADJUVANT_STEP_API_URL,
  UPSERT_APPLICATION_STEP_API_URL,
  UPSERT_APPLICATION_TASK_API_URL,
  UPSERT_BIOSTIMULANT_STEP_API_URL,
  UPSERT_CROP_ESTABLISHMENT_TASK_API_URL,
  UPSERT_CROP_PROTECTION_STEP_API_URL,
  UPSERT_GENERIC_STEP_API_URL,
  UPSERT_PRODUCTION_STEP_API_URL,
  UPSERT_PRODUCTION_TASK_API_URL,
  UPSERT_SEED_DRESS_STEP_API_URL,
  UPSERT_SEED_STEP_API_URL,
  UPSERT_SOIL_INPUT_STEP_API_URL,
} from 'constants/apiConstants';
import { IInput, InputType } from 'interfaces/IInput';
import { IInputStep, InputStepType } from 'interfaces/IInputStep';
import { IInputTask, InputTaskType } from 'interfaces/IInputTask';
import axiosInstance from 'services/AxiosInstance';
import { resolveApiPath } from 'utils/UtilityMethods';

interface UpdateInputDocResponse {
  data: IInput[];
  changes: {
    changeId: string;
    collection: string;
    docId: string;
    dateModified: string;
    modifierId: string;
    snapModifier: string;
    sourceId: string;
  }[];
}

interface UpsertTaskPayload {
  id: string;
  task: IInputTask;
}

interface UpsertStepPayload {
  id: string;
  taskId: string;
  step: IInputStep;
}

type TaskUpsertApiMap = Partial<Record<InputTaskType, string>>;
type StepUpsertApiMap = Partial<Record<InputStepType, string>>;

const TASK_UPSERT_API_BY_TYPE: Readonly<TaskUpsertApiMap> = {
  [InputTaskType.Application]: UPSERT_APPLICATION_TASK_API_URL,
  [InputTaskType.Production]: UPSERT_PRODUCTION_TASK_API_URL,
  [InputTaskType.CropEstablishment]: UPSERT_CROP_ESTABLISHMENT_TASK_API_URL,
};

const STEP_UPSERT_API_BY_TYPE: Readonly<StepUpsertApiMap> = {
  [InputStepType.Generic]: UPSERT_GENERIC_STEP_API_URL,
  [InputStepType.Application]: UPSERT_APPLICATION_STEP_API_URL,
  [InputStepType.CropProtection]: UPSERT_CROP_PROTECTION_STEP_API_URL,
  [InputStepType.Adjuvant]: UPSERT_ADJUVANT_STEP_API_URL,
  [InputStepType.SoilInput]: UPSERT_SOIL_INPUT_STEP_API_URL,
  [InputStepType.Biostimulant]: UPSERT_BIOSTIMULANT_STEP_API_URL,
  [InputStepType.Production]: UPSERT_PRODUCTION_STEP_API_URL,
  [InputStepType.Seed]: UPSERT_SEED_STEP_API_URL,
  [InputStepType.SeedDress]: UPSERT_SEED_DRESS_STEP_API_URL,
};

const withClientNowHeaders = (clientNow: string) => ({
  headers: {
    ClientNow: clientNow,
  },
});

const postWithClientNow = async <TPayload>(
  apiUrl: string,
  payload: TPayload,
  clientNow: string
): Promise<UpdateInputDocResponse> => {
  const response = await axiosInstance.post(
    apiUrl,
    payload,
    withClientNowHeaders(clientNow)
  );

  return response.data;
};

const deleteWithClientNow = async (
  apiUrl: string,
  clientNow: string
): Promise<UpdateInputDocResponse> => {
  const response = await axiosInstance.delete(
    apiUrl,
    withClientNowHeaders(clientNow)
  );

  return response.data;
};

const getTaskUpsertApiUrl = (taskType: InputTaskType): string => {
  const apiUrl = TASK_UPSERT_API_BY_TYPE[taskType];
  if (!apiUrl) {
    throw new Error(`Unsupported task type: ${taskType}`);
  }
  return apiUrl;
};

const getStepUpsertApiUrl = (stepType: InputStepType): string => {
  const apiUrl = STEP_UPSERT_API_BY_TYPE[stepType];
  if (!apiUrl) {
    throw new Error(`Unsupported step type: ${stepType}`);
  }
  return apiUrl;
};

const upsertTask = async (
  apiUrl: string,
  inputId: string,
  task: IInputTask,
  clientNow: string
): Promise<UpdateInputDocResponse> => {
  return postWithClientNow(
    apiUrl,
    {
      id: inputId,
      task,
    } as UpsertTaskPayload,
    clientNow
  );
};

const upsertStep = async (
  apiUrl: string,
  inputId: string,
  taskId: string,
  step: IInputStep,
  clientNow: string
): Promise<UpdateInputDocResponse> => {
  return postWithClientNow(
    apiUrl,
    {
      id: inputId,
      taskId,
      step,
    } as UpsertStepPayload,
    clientNow
  );
};

export const InputService = {
  /**
   * Fetches inputs for a specific organization with optional filtering by input type.
   *
   * Reads GetByOrgActive, so soft-deleted documents are excluded server-side
   * — the client only narrows by input type.
   *
   * @param orgId - The organization ID to fetch inputs for
   * @param inputType - Optional filter for input type (InputType.Reserved, InputType.PlanActual, InputType.Recommendation)
   * @returns Promise containing an array of active input documents, filtered by input type if specified
   */
  GetByOrgActive: async (
    orgId: string,
    inputType?: InputType
  ): Promise<IInput[]> => {
    const response = await axiosInstance.get<IInput[]>(
      `${GET_ACTIVE_INPUTS_BY_ORG_API_URL}/${orgId}`,
      { _silentErrors: true }
    );

    if (inputType !== undefined) {
      return response.data.filter((input) => input.inputType === inputType);
    }

    return response.data;
  },

  /**
   * Fetches an input document by its ID
   * @param id - The ID of the input document to fetch
   * @returns Promise containing the input document
   */
  GetById: async (id: string): Promise<IInput> => {
    const response = await axiosInstance.get(
      `${GET_INPUT_BY_ID_API_URL}/${id}`
    );
    return response.data;
  },

  /**
   * Creates a new input document. The id is client-generated, so the caller
   * can key UI state on it before the response lands.
   * @param inputDoc - Full input document payload
   * @param clientNow - ISO timestamp string for ClientNow header
   * @returns Promise containing the created input document and change records
   */
  CreateDoc: async (
    inputDoc: IInput,
    clientNow: string
  ): Promise<UpdateInputDocResponse> => {
    return postWithClientNow(CREATE_INPUT_DOC_API_URL, inputDoc, clientNow);
  },

  /**
   * Updates an input document.
   * @param inputDoc - Full input document payload
   * @param clientNow - ISO timestamp string for ClientNow header
   * @returns Promise containing the updated input document and change records
   */
  UpdateDoc: async (
    inputDoc: IInput,
    clientNow: string
  ): Promise<UpdateInputDocResponse> => {
    const response = await axiosInstance.put(
      UPDATE_INPUT_DOC_API_URL,
      inputDoc,
      withClientNowHeaders(clientNow)
    );

    return response.data;
  },

  /**
   * Deletes an input document. The backend is expected to soft-delete by
   * clearing the document's isActive flag rather than removing the record.
   * @param id - The ID of the input document to delete
   * @param clientNow - ISO timestamp string for ClientNow header
   * @returns Promise containing the updated input document and change records
   */
  Delete: async (
    id: string,
    clientNow: string
  ): Promise<UpdateInputDocResponse> => {
    return deleteWithClientNow(
      resolveApiPath(DELETE_INPUT_DOC_API_URL, { id }),
      clientNow
    );
  },

  UpsertTask: async (
    inputId: string,
    task: IInputTask,
    clientNow: string
  ): Promise<UpdateInputDocResponse> => {
    const apiUrl = getTaskUpsertApiUrl(task.taskType);
    return upsertTask(apiUrl, inputId, task, clientNow);
  },

  UpsertStep: async (
    inputId: string,
    taskId: string,
    step: IInputStep,
    clientNow: string
  ): Promise<UpdateInputDocResponse> => {
    const apiUrl = getStepUpsertApiUrl(step.stepType);
    return upsertStep(apiUrl, inputId, taskId, step, clientNow);
  },

  DeleteTask: async (
    inputId: string,
    taskId: string,
    clientNow: string
  ): Promise<UpdateInputDocResponse> => {
    return deleteWithClientNow(
      resolveApiPath(DELETE_INPUT_TASK_API_URL, { id: inputId, taskId }),
      clientNow
    );
  },

  DeleteStep: async (
    inputId: string,
    taskId: string,
    stepId: string,
    clientNow: string
  ): Promise<UpdateInputDocResponse> => {
    return deleteWithClientNow(
      resolveApiPath(DELETE_INPUT_STEP_API_URL, {
        id: inputId,
        taskId,
        stepId,
      }),
      clientNow
    );
  },
};

export default InputService;
